/* ============================================================
   eShopBoost — Cart cross-sell rail (Phase B)
   <eshopboost-xsell> custom element.
   - One-card-at-a-time carousel with prev/next + counter
   - Option dropdowns -> variant resolution from embedded JSON
   - Per-variant price / compare-at / availability updates
   - Add-to-cart mirroring Dawn's product-form flow
     (sections payload + cart-drawer renderContents)
   - Optional fetch of Shopify related recommendations via the
     Product Recommendations API (section: eshopboost-xsell-recs),
     swapped in over the server-rendered fallback content.
   The element re-connects automatically whenever Dawn re-renders
   the drawer, so no extra event wiring is needed.
   ============================================================ */
(() => {
  'use strict';

  if (customElements.get('eshopboost-xsell')) return;

  const recsCache = new Map();

  class EshopboostXsell extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-xsell-viewport]');
      this.counter = this.querySelector('[data-xsell-counter]');
      this.prevBtn = this.querySelector('[data-xsell-prev]');
      this.nextBtn = this.querySelector('[data-xsell-next]');
      if (!this.viewport) return;

      this.index = 0;
const nav = (event, dir) => {
        event.preventDefault();
        event.stopPropagation();
        this.step(dir);
      };
      if (this.prevBtn) this.prevBtn.addEventListener('click', (event) => nav(event, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', (event) => nav(event, 1));

      this.initCards();

const details = this.querySelector('[data-xsell-details]');
      if (details) {
        const mode = this.dataset.collapse || 'expanded';
        if (
          mode === 'collapsed' ||
          (mode === 'collapsed_mobile' && window.matchMedia('(max-width: 749px)').matches)
        ) {
          details.removeAttribute('open');
        }
      }
      if (this.dataset.source === 'recs' && this.dataset.productId && this.dataset.recsUrl) {
        this.loadRecommendations();
      }
    }

    cards() {
      return Array.from(this.querySelectorAll('[data-xsell-card]'));
    }

    initCards() {
      const cards = this.cards();
      cards.forEach((card) => this.wireCard(card));
      this.index = 0;
      this.update();
    }

    update() {
      const cards = this.cards();
      if (!cards.length) {
        if (this.dataset.source !== 'recs') this.hidden = true;
        return;
      }
      if (this.index >= cards.length) this.index = 0;
      cards.forEach((card, i) => card.classList.toggle('is-active', i === this.index));
      if (this.counter) this.counter.textContent = (this.index + 1) + ' / ' + cards.length;
      const many = cards.length > 1;
      if (this.prevBtn) this.prevBtn.hidden = !many;
      if (this.nextBtn) this.nextBtn.hidden = !many;
      this.removeAttribute('data-empty');
      this.hidden = false;
    }

    step(dir) {
      const count = this.cards().length;
      if (!count) return;
      this.index = (this.index + dir + count) % count;
      this.update();
    }

    wireCard(card) {
      if (card.dataset.xsellWired) return;
      card.dataset.xsellWired = 'true';

      let variants = [];
      const jsonEl = card.querySelector('[data-xsell-variants]');
      if (jsonEl) {
        try {
          variants = JSON.parse(jsonEl.textContent);
        } catch (error) {
          variants = [];
        }
      }

      const selects = Array.from(card.querySelectorAll('[data-xsell-option]'));
      const addBtn = card.querySelector('[data-xsell-add]');
      const priceEl = card.querySelector('[data-xsell-price]');
      const compareEl = card.querySelector('[data-xsell-compare]');
      if (!addBtn) return;

      const label = addBtn.textContent.trim();
      let formatter = null;
      try {
        formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });
      } catch (error) {
        formatter = null;
      }
      const money = (cents) => (formatter ? formatter.format(cents / 100) : (cents / 100).toFixed(2));

      const resolve = () => {
        if (!selects.length || !variants.length) return;
        const chosen = selects.map((s) => s.value);
        const variant = variants.find((v) =>
          chosen.every((value, i) => v['option' + (i + 1)] === value)
        );
        if (!variant) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
          return;
        }
        addBtn.dataset.variantId = variant.id;
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? label : 'Sold out';
        if (priceEl && typeof variant.price === 'number') priceEl.textContent = money(variant.price);
        if (compareEl) {
          const hasCompare =
            typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
          compareEl.hidden = !hasCompare;
          if (hasCompare) compareEl.textContent = money(variant.compare_at_price);
        }
      };

      selects.forEach((select) =>
        select.addEventListener('change', (event) => {
          event.stopPropagation();
          resolve();
        })
      );
      
      addBtn.addEventListener('click', () => this.addToCart(addBtn, label));
      resolve();
    }

    addToCart(addBtn, label) {
      const variantId = addBtn.dataset.variantId;
      if (!variantId || addBtn.disabled) return;

      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      addBtn.textContent = 'Adding…';

      const revert = (text) => {
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-busy');
        addBtn.textContent = text || label;
      };

      const config =
        typeof window.fetchConfig === 'function'
          ? window.fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (
        this.dataset.context === 'drawer' &&
        cart &&
        typeof cart.getSectionsToRender === 'function'
      ) {
        formData.append(
          'sections',
          cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }
      config.body = formData;

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      fetch(addUrl, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            revert('Error');
            window.setTimeout(() => revert(), 2000);
            return;
          }
          if (this.dataset.context === 'page') {
            window.location.reload();
            return;
          }
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            window.location.reload();
          }
        })
        .catch(() => {
          revert('Error');
          window.setTimeout(() => revert(), 2000);
        });
    }

    loadRecommendations() {
      const url =
        this.dataset.recsUrl +
        '?section_id=' +
        encodeURIComponent(this.dataset.sectionId || 'eshopboost-xsell-recs') +
        '&product_id=' +
        encodeURIComponent(this.dataset.productId) +
        '&limit=' +
        encodeURIComponent(this.dataset.limit || '4') +
        '&intent=related';

      const apply = (html) => {
        const holder = document.createElement('div');
        holder.innerHTML = html;
        const inner = holder.querySelector('[data-xsell-recs]');
        if (!inner) return;
        if (!inner.querySelector('[data-xsell-card]')) return;
        this.viewport.innerHTML = inner.innerHTML;
        this.initCards();
      };

      if (recsCache.has(url)) {
        apply(recsCache.get(url));
        return;
      }

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('recs fetch failed: ' + response.status);
          return response.text();
        })
        .then((text) => {
          recsCache.set(url, text);
          apply(text);
        })
        .catch(() => {
          /* keep the server-rendered fallback content */
        });
    }
  }

  customElements.define('eshopboost-xsell', EshopboostXsell);
})();